<template>
  <form novalidate test-id="form" @submit.prevent="submitType">
    <LsModal title track-id="review_modal">
      <!-- review body-->
      <template v-if="!cycleChangeStep" #body test-id="review">
        <div class="review-modal--heading">
          <div class="review-modal--heading-upper">
            <LsIcon v-if="screenSize.desktop" track-id="review_modal" type="checkmark" size="lg" />
            <h1 class="service-text">{{ renderServiceText }}</h1>
          </div>
          <p test-id="date" class="review_modal-date">{{ renderServiceDate | date }}</p>
        </div>
        <div class="review-modal--star-div">
          <p class="review-modal--please-rate">Please rate {{ contractorFirstName }}'s service</p>
          <LsStars
            v-model="rating"
            track-id="review_modal-rating"
            interactive
            size="xxlg"
            @input="checkRating"
          />
        </div>
        <BCollapse :open="Boolean(rating)">
          <div v-if="showNotes" class="review-modal--input">
            <LsInput
              v-model="notes"
              track-id="review_modal-feedback"
              type="textarea"
              name="review_modal-feedback"
              :label="getInputLabel"
            />
            <p v-if="rating === 5" class="review-modal--input-length">{{ notesLength }} / 250</p>
          </div>
          <div v-if="showIssueTypes" class="review-modal--options">
            <LsRadios
              v-model="issue_type"
              label="Please leave feedback about the service"
              :options="options"
              test-id="quality"
              track-id="review_modal-issue_type"
              name="issue_type"
              :validation="{ required: true }"
              columns
            />
          </div>
        </BCollapse>
        <BCollapse :open="Boolean(rating >= 4)">
          <div v-if="rating >= 4" class="review-modal--tipping">
            <div class="review-modal--prompt">
              <p :class="['review-modal--add-a-tip']">Add a tip for {{ contractorFirstName }}</p>
              <p v-if="tipMatchEligible" class="review-modal--match-your-tip">
                {{ brandName }} will match your tip!
              </p>
              <p v-if="tipMatchEligible && !tip" class="review-modal--explain-tip-match">
                For example, if you give {{ contractorFirstName }} $10, {{ brandName }} will add
                another $10.
              </p>
              <p v-if="tipMatchEligible && tip" class="review-modal--explain-tip-match">
                You tip {{ tipAmount | currency }} and the provider will receive an additional
                {{ tipAmount | currency }} from {{ brandName }}!
              </p>
            </div>
            <ReviewTipping
              v-model="tip"
              :tipping-options="tippingOptions"
              track-id="review_modal-tipping"
              :job-price="jobPrice"
              @other-amount="setOtherAmount"
            />
          </div>
        </BCollapse>
      </template>

      <!-- instant quote cycle change body -->
      <template v-else-if="cycleChangeStep === 1" #body>
        <h1 class="review-modal--cycle-change-title">
          {{ `Schedule ${iqName} on a regular basis at ${street}` }}
        </h1>
        <LsRadios
          v-model="cycle"
          test-id="cycle-change"
          :options="instantQuoteCycles"
          name="cycle"
          :validation="{ required: true }"
          track-id="review_modal-cycle"
        />
      </template>

      <!-- instant quote accept/reject body -->
      <template v-else #body>
        <div>
          <h1>Review Quote</h1>
          <InstantQuoteDetailsCard
            :current-quote="instantQuote.currentQuote"
            :cycle="cycle"
            :name="schedule.contractor.name"
            :edit-button-method="modifyCycleChangeStep"
          />
        </div>
      </template>

      <!-- review footer -->
      <template v-if="!cycleChangeStep" #footer>
        <BCollapse :open="Boolean(rating)">
          <LsButton
            test-id="submit-buttom"
            track-id="review_modal-submit"
            text="submit rating"
            :loading="review.loadingStatus.isUpdating"
            type="submit"
          />
        </BCollapse>
      </template>

      <!-- instant quote cycle change footer -->
      <template v-else-if="cycleChangeStep === 1" #footer>
        <LsButton
          test-id="next"
          track-id="review_modal-next"
          text="next"
          :loading="instantQuote.loadingStatus.isUpdating"
          type="submit"
        />
      </template>

      <!-- instant quote accept/reject footer -->
      <template v-else #footer>
        <div class="review-modal--accept-reject-buttons">
          <span class="review-modal--decline-quote">
            <LsButton
              track-id="review_modal-decline_instant_quote"
              text="decline quote"
              bg-color="primary-light"
              text-color="primary"
              :loading="rejectQuoteIsUpdating"
              @click="rejectQuote"
            />
          </span>
          <LsButton
            track-id="review_modal-accept_instant_quote"
            text="accept quote"
            :loading="acceptQuoteIsUpdating"
            @click="acceptQuote"
          />
        </div>
      </template>
    </LsModal>
  </form>
</template>

<script>
import { LsModal, LsStars, LsIcon, LsInput, LsButton, LsRadios } from '@/components/common';
import _ from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import { lsScheduleHelpers, lsRouterHelpers } from 'ls-vue-common';
import { InstantQuoteConstants } from 'ls-constants';
import {
  REVIEW__REVIEW_SERVICE,
  REVIEW__GET_TIPPING_OPTIONS,
  INSTANT_QUOTE__GENERATE_QUOTE,
  INSTANT_QUOTE__ACCEPT_QUOTE,
  INSTANT_QUOTE__REJECT_QUOTE,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
} from '@/store/types';
import ReviewTipping from './ReviewTipping/ReviewTipping.vue';
import VueTypes from 'vue-types';
import InstantQuoteDetailsCard from './components/InstantQuoteDetailsCard.vue';
import { themedComponentMixin } from '@/mixins';

export default {
  name: 'ReviewModal',
  components: {
    LsModal,
    LsStars,
    LsIcon,
    LsInput,
    LsButton,
    LsRadios,
    ReviewTipping,
    InstantQuoteDetailsCard,
  },
  mixins: [themedComponentMixin],
  $_veeValidate: {
    validator: 'new',
  },
  props: {
    //eslint-disable-next-line vue/prop-name-casing
    scheduleevent_id: VueTypes.number.isRequired,
    //eslint-disable-next-line vue/prop-name-casing
    schedule_id: VueTypes.number.isRequired,
    contractorFirstName: VueTypes.string.isRequired,
    tipMatchEligible: VueTypes.bool,
  },
  data() {
    return {
      notes: '',
      rating: 0,
      issue_type: null,
      schedule: {},
      property_id: null,
      tip: '',
      otherAmountEntered: false,
      options: [
        {
          label: 'My Pro did a poor job',
          value: 'quality',
        },
        {
          label: 'My Pro never showed up',
          value: 'no_work_done',
        },
        {
          label: 'My Pro only completed part of the job',
          value: 'partial_complete',
        },
        {
          label: 'There was damage to my property',
          value: 'property_damage',
        },
      ],
      cycleChangeStep: 0,
      cycle: 28,
      rejectQuoteIsUpdating: false,
      acceptQuoteIsUpdating: false,
    };
  },
  computed: {
    ...mapState(['schedules', 'screenSize', 'review', 'instantQuote']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),
    street() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id).street;
    },
    submitType() {
      if (!this.cycleChangeStep) {
        return this.onSubmit;
      }

      return this.generateNewInstantQuote;
    },
    renderServiceText() {
      return `Your ${_.toLower(
        lsScheduleHelpers.getScheduleServiceName({ schedule: this.schedule })
      )} is complete!`;
    },
    renderServiceDate() {
      return _.get(this.scheduleEvent, 'status_set_at');
    },
    showPercentage() {
      return _.get(this.review.tippingOptions, 'show') === 'percentage' ? true : false;
    },
    tippingOptions() {
      const { show, percentages, amounts } = this.review.tippingOptions;

      return {
        show,
        percentages,
        amounts,
      };
    },
    jobPrice() {
      return _.find(this.schedule.scheduleevents, {
        scheduleevent_id: this.scheduleevent_id,
      }).price;
    },
    tipAmount() {
      return this.otherAmountEntered ? this.tip : _.round(this.jobPrice * (this.tip / 100), 2);
    },
    getInputLabel() {
      if (this.rating === 5) {
        return `Leave an epic 250 character review and we’ll feature ${
          this.contractorFirstName
        } on our site!`;
      }
      if (this.rating <= 4) {
        return 'Additional comments?';
      }

      return '';
    },
    isMowingFixOrService() {
      return (
        _.get(this.schedule, 'service.str_id') === 'mowing' ||
        _.get(this.schedule, 'qualityassurance.service.str_id') === 'mowing'
      );
    },
    showNotes() {
      if (!this.rating || (this.rating < 4 && this.isMowingFixOrService)) {
        return false;
      }
      return true;
    },
    notesLength() {
      return this.notes.length;
    },
    showIssueTypes() {
      if (!this.rating || this.rating > 3 || !this.isMowingFixOrService) {
        return false;
      }
      return true;
    },
    instantQuoteCycles() {
      return _.reject(InstantQuoteConstants.cycleOptions, { value: 0 });
    },
    iqType() {
      return this.schedule.instant_quote.type;
    },
    iqName() {
      return _.toLower(this.schedule.instant_quote[this.iqType].service.name);
    },
    formData() {
      const keysToRemove = ['copy', 'images', 'created_at', 'id', 'service', 'updated_at', 'cycle'];
      const cleanedFormData = { ...this.schedule.instant_quote[this.iqType] };

      _.forEach(cleanedFormData, (value, key) => {
        if (_.includes(keysToRemove, key)) {
          _.unset(cleanedFormData, key);
        }
      });

      return {
        ...cleanedFormData,
        cycle: this.cycle,
      };
    },
  },
  created() {
    this.schedule = this.schedules.schedulesById[this.schedule_id];
    this.scheduleEvent =
      _.find(this.schedule.scheduleevents, {
        scheduleevent_id: _.toNumber(this.scheduleevent_id),
      }) || {};

    this.contractorFirstName = _.capitalize(this.contractorFirstName) || 'your crew';

    this.property_id = this.schedule.property_id;
    this[REVIEW__GET_TIPPING_OPTIONS]({
      property_id: this.schedule.property_id,
      schedule_id: this.schedule.schedule_id,
    });
  },
  methods: {
    ...mapActions([
      REVIEW__REVIEW_SERVICE,
      REVIEW__GET_TIPPING_OPTIONS,
      INSTANT_QUOTE__GENERATE_QUOTE,
      INSTANT_QUOTE__ACCEPT_QUOTE,
      INSTANT_QUOTE__REJECT_QUOTE,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
    ]),
    checkRating() {
      if (this.rating < 5) {
        this.tip = '';
      }
      if (this.rating >= 4) {
        this.issue_type = null;
      }
      if (this.rating <= 3) {
        this.notes = '';
      }
    },
    setOtherAmount(value) {
      this.otherAmountEntered = value;
    },
    async onSubmit() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this[REVIEW__REVIEW_SERVICE]({
        property_id: this.property_id,
        schedule_id: this.schedule_id,
        scheduleevent_id: this.scheduleevent_id,
        rating: this.rating,
        notes: this.notes,
        issue_type: _.get(this, 'issue_type', null),
        share_with_support: false,
        tip_show: this.review.tippingOptions.show,
        // make sure show === percentage, tip is truthy, and manual tip was not entered by user
        tip_percentage:
          this.tip && this.showPercentage && !this.otherAmountEntered ? _.toNumber(this.tip) : null,
        // make sure show === amount and tip is truthy OR manual tip was entered by user, and tip is truthy
        tip_amount:
          this.tip && (!this.showPercentage || this.otherAmountEntered)
            ? _.toNumber(this.tip)
            : null,
        onSuccess: this.onReviewSuccess,
      });
    },
    onReviewSuccess(response) {
      if (_.get(response, 'cycle_change_offered_at')) {
        this.modifyCycleChangeStep(1);
        return;
      }

      this.$parent.close();
    },
    modifyCycleChangeStep(step) {
      this.cycleChangeStep = step;
    },
    async generateNewInstantQuote() {
      await this[INSTANT_QUOTE__GENERATE_QUOTE]({
        property_id: this.property_id,
        type: this.iqType,
        formData: {
          ...this.formData,
          copy_images_from_existing_instant_quotable_id: this.schedule.instant_quote[this.iqType]
            .id,
        },
        onSuccess: () => {
          this.modifyCycleChangeStep(2);
        },
      });
    },
    async rejectQuote() {
      this.rejectQuoteIsUpdating = true;

      await this[INSTANT_QUOTE__REJECT_QUOTE]({
        property_id: this.property_id,
        formData: this.formData,
        onSuccess: this.onRejectSuccess,
      });

      this.rejectQuoteIsUpdating = false;
    },
    onRejectSuccess() {
      this.$router.push({
        name: 'services',
      });
      this.$parent.close();
    },
    async acceptQuote() {
      this.acceptQuoteIsUpdating = true;

      await this[INSTANT_QUOTE__ACCEPT_QUOTE]({
        property_id: this.property_id,
        onSuccess: this.onAcceptSuccess,
      });

      this.acceptQuoteIsUpdating = false;
    },
    onAcceptSuccess() {
      if (lsRouterHelpers.getName(this.$route) === 'services') {
        this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]({ forceRefresh: true });
      } else {
        this.$router.push({
          name: 'services',
        });
      }
      this.$parent.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.service-text {
  padding-left: 8px;
}
.review-modal--heading {
  display: flex;
  flex-direction: column;
}

.review-modal--heading-upper {
  display: flex;
}

.review_modal-date {
  padding: 4px 0 15px 0;
  font-weight: bold;
}

.review-modal--please-rate,
.review-modal--add-a-tip {
  color: $primary;
  font-weight: bold;
}
.review-modal--match-your-tip {
  font-weight: bold;
}
.review-modal--match-your-tip,
.review-modal--explain-tip-match {
  padding-bottom: 12px;
}

.review-modal--star-div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0px;
  border-top: 1px solid $primary-light;
  border-bottom: 1px solid $primary-light;
}

.review-modal--input {
  padding: 22px 0 14px 0;
}

.review-modal--input-length {
  width: 100%;
  text-align: right;
  color: $gray-secondary;
  font-weight: bold;
}

.review-modal--options {
  padding: 20px 0px;
  display: flex;
}

.review-modal--tipping {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid $primary-light;
  margin-top: 5px;
}
.review-modal--add-a-tip {
  padding-top: 12px;
  padding-bottom: 12px;
}

.review-modal--cycle-change-title {
  margin-bottom: 20px;
}

.review-modal--decline-quote {
  margin-right: 10px;
  @media (max-width: $mobile-max-width) {
    margin-right: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: $mobile-max-width) {
  h1 {
    text-align: center;
  }
  .review-modal--heading {
    display: flex;
    align-items: center;
  }

  .review-modal--star-div {
    flex-direction: column;
  }

  .review-modal--please-rate {
    padding: 10px 0;
  }

  .review-modal--options {
    display: block;
  }

  .review-modal--tipping {
    padding: 10px 0 20px 0;
    flex-direction: column;
    align-items: center;
    border-top: none;
  }

  .review-modal--prompt {
    text-align: center;
  }

  .review-modal--accept-reject-buttons {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
}
.themed-app {
  .review-modal--please-rate,
  .review-modal--add-a-tip {
    color: $theme-color-primary;
  }
  .review-modal--star-div {
    border-color: $theme-color-primary-light;
  }
  .review-modal--input-length {
    color: $theme-color-gray-dark;
  }

  .review-modal--tipping {
    border-top: 1px solid $theme-color-primary-light;
  }
}
</style>
